import { subWeeks, parseJSON, isBefore } from "date-fns";
import { MandatorType } from "@bn/theming";

export interface InstrumentRedirectInput {
  type?: string;
  isin?: string;
  hasValidIsin?: boolean;
  quoteProvider?: string;
  quote?: { lastDateTime?: string | null };
}

export interface MandatorLinks {
  baseUrl: string;
  supportUrl: string;
  depotConnectUrl?: string;
  communityDiscussionsUrl?: string;
  openDepotUrl?: string;
  premiumLandingUrl?: string;
  logoutRedirectUrl?: string;
  getInstrumentDetailUrl?: (input: InstrumentRedirectInput) => string | null;
}

const hasUsableIsin = (input: InstrumentRedirectInput): boolean => {
  if (!input.isin) return false;
  if (input.hasValidIsin !== undefined) return input.hasValidIsin;
  return !input.isin.startsWith("_NA_");
};

const UNSUPPORTED_INSTRUMENT_TYPES = new Set([
  "41_cash",
  "71_massets",
  "91_managed",
]);

interface InstrumentUrlBuilderOptions {
  baseUrl: string;
  typeToCategory?: Record<string, string | null>;
  buildPath?: (category: string, isin: string) => string;
  transformIsin?: (isin: string) => string;
  buildUrl?: (isin: string) => string;
}

const buildInstrumentUrl =
  ({
    baseUrl,
    typeToCategory,
    buildPath,
    transformIsin,
    buildUrl,
  }: InstrumentUrlBuilderOptions) =>
  (input: InstrumentRedirectInput): string | null => {
    if (!hasUsableIsin(input)) return null;
    if (!input.quoteProvider || input.quoteProvider === "none") return null;
    if (
      input.quote !== undefined &&
      (!input.quote.lastDateTime ||
        isBefore(parseJSON(input.quote.lastDateTime), subWeeks(new Date(), 2)))
    )
      return null;
    if (input.type === "81_fcurr" && input.quoteProvider === "crypto")
      return null;
    if (input.type && UNSUPPORTED_INSTRUMENT_TYPES.has(input.type)) return null;
    const isin = transformIsin ? transformIsin(input.isin!) : input.isin!;
    if (buildUrl) return buildUrl(isin);
    if (!typeToCategory || !buildPath || !input.type)
      return `${baseUrl}/${isin}`;
    const category = typeToCategory[input.type];
    if (!category) return null;
    return `${baseUrl}${buildPath(category, isin)}`;
  };

const BOERSENNEWS_BASE_URL = "https://www.boersennews.de";
export const ARIVA_BASE_URL = "https://www.ariva.de";
const WSO_BASE_URL = "https://www.wallstreet-online.de";
const WSO_SMARTBROKER_TRACKING_BASE =
  "https://dwbdiv.smartbrokerplus.de/ts/94450/tsc";
const WSO_SMARTBROKER_AMC =
  "performance.smartbrokerplus.503409.568098.CRTiLl77G8t";

export const toArivaUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  try {
    return new URL(url, ARIVA_BASE_URL).toString();
  } catch {
    return undefined;
  }
};

export const mandatorLinks: Record<MandatorType, MandatorLinks> = {
  boersennews_de: {
    baseUrl: BOERSENNEWS_BASE_URL,
    supportUrl: "mailto:info@boersennews.de",
    depotConnectUrl: `${BOERSENNEWS_BASE_URL}/depot-verbinden/`,
    communityDiscussionsUrl: `${BOERSENNEWS_BASE_URL}/community/diskussionen/`,
    openDepotUrl: "https://www.smartbrokerplus.de/de-de/",
    premiumLandingUrl: "https://get.smartbrokerplus.de/boersennews-premium/",
    logoutRedirectUrl: `${BOERSENNEWS_BASE_URL}/nutzer/portfolioplus/`,
    getInstrumentDetailUrl: buildInstrumentUrl({
      baseUrl: BOERSENNEWS_BASE_URL,
      typeToCategory: {
        "11_stock": "aktien",
        "22_etf": "fonds",
        "21_fund": "fonds",
        "31_bond": "anleihen",
        "51_certos": "derivate/zertifikate",
        "61_pmetals": "rohstoffe",
        "81_fcurr": "devisen",
        "41_cash": null,
        "71_massets": null,
        "91_managed": null,
      },
      buildPath: (category, isin) => `/markt/${category}/detail/${isin}/`,
    }),
  },
  ariva_de: {
    baseUrl: ARIVA_BASE_URL,
    supportUrl: `${ARIVA_BASE_URL}/fehler_melden/?url=`,
    // No ariva-specific premium landing page yet — fall back to the generic
    // Smartbroker+ page for the "open a depot" option in the premium modal.
    openDepotUrl: "https://www.smartbrokerplus.de/de-de/",
    logoutRedirectUrl: "/api/auth/logout",
    getInstrumentDetailUrl: buildInstrumentUrl({
      baseUrl: ARIVA_BASE_URL,
    }),
  },
  wso: {
    baseUrl: WSO_BASE_URL,
    supportUrl: "mailto:feedback@wallstreet-online.de ",
    openDepotUrl: `${WSO_SMARTBROKER_TRACKING_BASE}?typ=r&amc=${WSO_SMARTBROKER_AMC}`,
    communityDiscussionsUrl: `${WSO_BASE_URL}/community/`,
    getInstrumentDetailUrl: buildInstrumentUrl({
      baseUrl: WSO_SMARTBROKER_TRACKING_BASE,
      buildUrl: (isin) =>
        `${WSO_SMARTBROKER_TRACKING_BASE}?typ=r&amc=${WSO_SMARTBROKER_AMC}&smc1=wealthapi-web&rmd=3&&trg=https://app.smartbrokerplus.de/assets?isin=${isin}`,
    }),
  },
};
